'use client';

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  readIdentityStorage,
  recordIdentityError,
  recordIdentityLifecycle,
} from '@/reporting/identityDiagnostics';

import { randomString, visitor } from '@/utils/tools';
import { safeLocalStorage, safeSessionStorage } from '@/utils/browser-storage';
import { useContext, useEffect } from 'react';

export const useStorageDataInit = () => {
  const setCanBuyCartsList = useStore((state) => state.setCanBuyCartsList);
  const setCanBuyList = useStore((state) => state.setCanBuyList);
  const setVisitorId = useStore((state) => state.setVisitorId);
  const setWaiMode = useStore((state) => state.setWaiMode);

  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);

  useEffect(() => {
    recordIdentityLifecycle('storage_init_started');
    let canBuyListStorage: string | null = null;
    let canBuyCartsListStorage: string | null = null;
    try {
      canBuyListStorage = window?.localStorage?.getItem('zl-canBuyList') || null;
      canBuyCartsListStorage = window?.localStorage?.getItem('zl-canBuyCartsList') || null;
    } catch (error) {
      recordIdentityError('cart_storage_read');
      throw error;
    }
    if (canBuyListStorage) {
      try {
        setCanBuyList(JSON.parse(canBuyListStorage) as []);
      } catch (error) {
        recordIdentityError('can_buy_list_parse');
        throw error;
      }
    }
    if (canBuyCartsListStorage) {
      try {
        setCanBuyCartsList(JSON.parse(canBuyCartsListStorage) as []);
      } catch (error) {
        recordIdentityError('can_buy_carts_list_parse');
        throw error;
      }
    }
    const visitorId = readIdentityStorage('zl-visitor_id', 'storage_init') || visitor();
    recordIdentityLifecycle('visitor_id_resolved', {
      diag_presence: visitorId ? 'present' : 'missing',
    });
    setVisitorId(visitorId);
    recordIdentityLifecycle('visitor_id_committed', {
      diag_presence: visitorId ? 'present' : 'missing',
    });

    const blindMode = safeLocalStorage.getItem('zl-blind-mode');

    if (blindMode) {
      setWaiMode(blindMode === 'true');
    }

    if (inAppH5) {
      const queryParams = new URLSearchParams(window.location.search);
      safeSessionStorage.setItem('zl-distinct_id', queryParams.get('distinct_id') || '');
      safeSessionStorage.setItem('zl-anonymous_id', queryParams.get('anonymous_id') || '');
      const appQuery = {};
      ['app_currency_code', 'version', 'store_id', 'source'].forEach((key) => {
        if (queryParams.get(key)) {
          appQuery[key] = queryParams.get(key);
        }
      });

      safeSessionStorage.setItem('zl-appQuery', JSON.stringify(appQuery));
    }

    const isFirstVisit = safeSessionStorage.getItem('zl-is-first-visit');
    if (!isFirstVisit) {
      safeSessionStorage.setItem('zl-is-first-visit', 'true');
      const aleid = randomString(40);
      safeLocalStorage.setItem('zl_axon_aleid', aleid);
    }
  }, []);
};
